/**
 * ScheduleBuilder — constructor de periodicidad del editor de rutinas
 * (REQ-FRT-002 §4; revisión UI del 18-Oct-2026).
 *
 * La UI no expone la jerga RRULE: el editor compone la representación
 * canónica (RRULE + zona IANA + dtstart/fin/count) desde opciones
 * seleccionables, muestra un resumen legible, las próximas ejecuciones
 * (vista previa del backend) y la zona horaria como desplegable.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Label, Select, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useSchedulePreview } from '../hooks/useFrontier';
import type { SchedulePreview, ScheduleTriggerSpec } from '../types';

export type ScheduleFrequency =
  | 'once'
  | 'minutes'
  | 'hours'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly';

export interface ScheduleBuilderState {
  frequency: ScheduleFrequency;
  interval: number;
  date: string;
  time: string;
  weekdays: string[];
  monthlyMode: 'day' | 'last' | 'nth';
  monthDay: number;
  nth: 1 | 2 | 3 | 4 | -1;
  nthWeekday: string;
  yearMonth: number;
  yearDay: number;
  startDate: string;
  end: 'never' | 'until' | 'count';
  untilDate: string;
  count: number;
  timezone: string;
}

const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
const WEEKDAY_KEYS: Record<string, string> = {
  MO: 'frontier.editor.weekdayMon',
  TU: 'frontier.editor.weekdayTue',
  WE: 'frontier.editor.weekdayWed',
  TH: 'frontier.editor.weekdayThu',
  FR: 'frontier.editor.weekdayFri',
  SA: 'frontier.editor.weekdaySat',
  SU: 'frontier.editor.weekdaySun',
};

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Madrid',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Bogota',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function ianaTimezones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (supported && supported.length > 0) return supported;
  } catch {
    // Fallback below (older engines)
  }
  return FALLBACK_TIMEZONES;
}

export function defaultScheduleState(timezone = browserTimezone()): ScheduleBuilderState {
  return {
    frequency: 'daily',
    interval: 30,
    date: '',
    time: '09:00',
    weekdays: ['MO'],
    monthlyMode: 'day',
    monthDay: 1,
    nth: 1,
    nthWeekday: 'MO',
    yearMonth: 1,
    yearDay: 1,
    startDate: '',
    end: 'never',
    untilDate: '',
    count: 5,
    timezone,
  };
}

// ---------------------------------------------------------------------------
// Canonical composition (UI state → RRULE + timezone + dtstart/end)
// ---------------------------------------------------------------------------

function tzOffsetMs(utcMs: number, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const values: Record<string, number> = {};
  for (const part of formatter.formatToParts(new Date(utcMs))) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(
    values.year,
    (values.month ?? 1) - 1,
    values.day ?? 1,
    (values.hour ?? 0) % 24,
    values.minute ?? 0,
    values.second ?? 0,
  );
  return asUtc - utcMs;
}

/** Wall time (date + HH:MM in an IANA zone) → UTC instant. */
export function zonedToUtc(date: string, time: string, timeZone: string): string | null {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const utcGuess = Date.UTC(year, month - 1, day, hours, minutes);
  const firstOffset = tzOffsetMs(utcGuess, timeZone);
  let instant = utcGuess - firstOffset;
  const secondOffset = tzOffsetMs(instant, timeZone);
  if (secondOffset !== firstOffset) instant = utcGuess - secondOffset;
  return new Date(instant).toISOString();
}

export function composeRrule(state: ScheduleBuilderState): string | null {
  const weekdays = state.weekdays.filter((day) => WEEKDAY_CODES.includes(day as never));
  switch (state.frequency) {
    case 'once':
      return 'FREQ=DAILY';
    case 'minutes':
      return `FREQ=MINUTELY;INTERVAL=${Math.max(1, state.interval)}`;
    case 'hours':
      return `FREQ=HOURLY;INTERVAL=${Math.max(1, state.interval)}`;
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekly':
      return weekdays.length > 0 ? `FREQ=WEEKLY;BYDAY=${weekdays.join(',')}` : null;
    case 'monthly':
      if (state.monthlyMode === 'last') return 'FREQ=MONTHLY;BYMONTHDAY=-1';
      if (state.monthlyMode === 'nth') {
        const prefix = state.nth === -1 ? '-1' : String(state.nth);
        return `FREQ=MONTHLY;BYDAY=${prefix}${state.nthWeekday}`;
      }
      return `FREQ=MONTHLY;BYMONTHDAY=${Math.min(31, Math.max(1, state.monthDay))}`;
    case 'yearly':
      return `FREQ=YEARLY;BYMONTH=${state.yearMonth};BYMONTHDAY=${Math.min(
        31,
        Math.max(1, state.yearDay),
      )}`;
    default:
      return null;
  }
}

export function scheduleStateToSpec(state: ScheduleBuilderState): ScheduleTriggerSpec | null {
  const rrule = composeRrule(state);
  if (!rrule || !state.timezone) return null;

  const needsDate = state.frequency === 'once';
  // Frequencies without a visible clock field (minutely/hourly) anchor at the
  // current instant; the rest carry the chosen time-of-day.
  const usesClockTime = state.frequency !== 'minutes' && state.frequency !== 'hours';
  const date = needsDate ? state.date : state.startDate;
  if (needsDate && (!state.date || !state.time)) return null;

  let dtstart = date ? zonedToUtc(date, state.time || '00:00', state.timezone) : null;
  if (!dtstart && !needsDate && usesClockTime) {
    // RRULE has no BYHOUR: the occurrence time-of-day comes from DTSTART, so a
    // recurring schedule with a chosen hour must anchor at that hour (today in
    // the chosen zone; the planner takes the next future occurrence). Without
    // it the schedule would fire at the anchor tick's time-of-day (finding D4
    // de la validación e2e r3).
    const today = localIso(new Date(), state.timezone).date;
    dtstart = zonedToUtc(today, state.time, state.timezone);
  }
  if (needsDate && !dtstart) return null;

  if (state.end === 'until') {
    if (!state.untilDate) return null;
    const until = zonedToUtc(state.untilDate, '23:59', state.timezone);
    if (!until) return null;
    return { rrule, timezone: state.timezone, dtstart, end: 'until', until };
  }
  if (state.end === 'count') {
    return {
      rrule,
      timezone: state.timezone,
      dtstart,
      end: 'count',
      count: Math.max(1, state.count),
    };
  }
  if (needsDate) {
    // «Una vez» sin fin explícito: una única ocurrencia.
    return { rrule, timezone: state.timezone, dtstart, end: 'count', count: 1 };
  }
  return { rrule, timezone: state.timezone, dtstart, end: 'never' };
}

/** Best-effort parse of a stored schedule back into builder state (editing). */
export function specToScheduleState(spec: ScheduleTriggerSpec): ScheduleBuilderState {
  const state = defaultScheduleState(spec.timezone || browserTimezone());
  const rrule = spec.rrule ?? '';
  const dtstart = spec.dtstart ? new Date(spec.dtstart) : null;
  if (dtstart) {
    const iso = localIso(dtstart, state.timezone);
    state.date = iso.date;
    state.time = iso.time;
  }
  state.end = spec.end === 'until' ? 'until' : spec.end === 'count' && spec.count !== 1 ? 'count' : spec.end === 'count' && spec.count === 1 && dtstart ? 'never' : (spec.end ?? 'never');
  if (spec.until) state.untilDate = localIso(new Date(spec.until), state.timezone).date;
  if (spec.count) state.count = spec.count;

  const match = /^FREQ=(\w+)(?:;INTERVAL=(\d+))?(?:;BYDAY=([A-Z,]+))?(?:;BYMONTHDAY=(-?\d+))?(?:;BYMONTH=(\d+))?/.exec(
    rrule,
  );
  if (!match) return state;
  const [, freq, interval, byday, bymonthday, bymonth] = match;
  if (freq === 'DAILY' && spec.end === 'count' && spec.count === 1) {
    state.frequency = 'once';
    return state;
  }
  if (freq === 'MINUTELY') {
    state.frequency = 'minutes';
    state.interval = Number(interval ?? 30);
    return state;
  }
  if (freq === 'HOURLY') {
    state.frequency = 'hours';
    state.interval = Number(interval ?? 1);
    return state;
  }
  if (freq === 'WEEKLY' && byday) {
    state.frequency = 'weekly';
    state.weekdays = byday.split(',').filter(Boolean);
    return state;
  }
  if (freq === 'MONTHLY' && bymonthday) {
    state.frequency = 'monthly';
    if (byday && /^-?\d[A-Z]{2}$/.test(byday)) {
      state.monthlyMode = 'nth';
      state.nthWeekday = byday.slice(-2);
      const prefix = byday.slice(0, -2);
      state.nth = prefix === '-1' ? -1 : (Math.min(4, Math.max(1, Number(prefix))) as 1 | 2 | 3 | 4);
    } else if (bymonthday === '-1') {
      state.monthlyMode = 'last';
    } else {
      state.monthlyMode = 'day';
      state.monthDay = Number(bymonthday);
    }
    return state;
  }
  if (freq === 'YEARLY') {
    state.frequency = 'yearly';
    if (bymonth) state.yearMonth = Number(bymonth);
    if (bymonthday) state.yearDay = Number(bymonthday);
    return state;
  }
  state.frequency = 'daily';
  return state;
}

function localIso(instant: Date, timeZone: string): { date: string; time: string } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const values: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') values[part.type] = part.value;
  }
  const hour = (Number(values.hour) % 24).toString().padStart(2, '0');
  return {
    date: `${values.year}-${values.month}-${values.day}`,
    time: `${hour}:${values.minute}`,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ScheduleBuilderProps {
  value: ScheduleBuilderState;
  onChange: (next: ScheduleBuilderState) => void;
}

export function ScheduleBuilder({ value, onChange }: ScheduleBuilderProps) {
  const { t, locale } = useI18n();
  const preview = useSchedulePreview();
  const [previewData, setPreviewData] = useState<SchedulePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const spec = useMemo(() => scheduleStateToSpec(value), [value]);
  const timezones = useMemo(() => ianaTimezones(), []);

  // Debounced preview of the next occurrences (REQ-FRT-002 §4).
  useEffect(() => {
    setPreviewError(null);
    if (!spec) {
      setPreviewData(null);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      preview
        .mutateAsync({ ...spec, occurrences: 5 })
        .then((data) => setPreviewData(data))
        .catch(() => {
          setPreviewData(null);
          setPreviewError(t('frontier.editor.previewError'));
        });
    }, 500);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(spec)]);

  const set = <K extends keyof ScheduleBuilderState>(key: K, next: ScheduleBuilderState[K]) =>
    onChange({ ...value, [key]: next });

  const formatOccurrence = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone: value.timezone,
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const summary = useMemo(() => renderSummary(t, locale, value), [t, locale, value]);

  const timeField = (
    <div className="space-y-2">
      <Label htmlFor="schedule-time">{t('frontier.editor.time')}</Label>
      <Input
        id="schedule-time"
        data-testid="schedule-time"
        type="time"
        value={value.time}
        onChange={(event) => set('time', event.target.value)}
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="schedule-builder">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="schedule-frequency">{t('frontier.editor.periodicity')}</Label>
          <Select
            value={value.frequency}
            onValueChange={(next) => set('frequency', next as ScheduleFrequency)}
            options={[
              { value: 'once', label: t('frontier.editor.periodicityOnce') },
              { value: 'minutes', label: t('frontier.editor.periodicityMinutes') },
              { value: 'hours', label: t('frontier.editor.periodicityHours') },
              { value: 'daily', label: t('frontier.editor.periodicityDaily') },
              { value: 'weekly', label: t('frontier.editor.periodicityWeekly') },
              { value: 'monthly', label: t('frontier.editor.periodicityMonthly') },
              { value: 'yearly', label: t('frontier.editor.periodicityYearly') },
            ]}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="schedule-timezone">{t('frontier.editor.timezone')}</Label>
          <Select
            value={value.timezone}
            onValueChange={(next) => set('timezone', next)}
            options={timezones.map((zone) => ({ value: zone, label: zone }))}
          />
        </div>
      </div>

      {value.frequency === 'once' && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="schedule-date">{t('frontier.editor.date')}</Label>
            <Input
              id="schedule-date"
              data-testid="schedule-date"
              type="date"
              value={value.date}
              onChange={(event) => set('date', event.target.value)}
            />
          </div>
          {timeField}
        </div>
      )}

      {(value.frequency === 'minutes' || value.frequency === 'hours') && (
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="schedule-interval">{t('frontier.editor.every')}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="schedule-interval"
              data-testid="schedule-interval"
              type="number"
              min={1}
              value={value.interval}
              onChange={(event) => set('interval', Number(event.target.value))}
            />
            <span className="text-sm text-gray-500">
              {t(
                value.frequency === 'minutes'
                  ? 'frontier.editor.minutesUnit'
                  : 'frontier.editor.hoursUnit',
              )}
            </span>
          </div>
        </div>
      )}

      {value.frequency === 'daily' && <div className="max-w-xs">{timeField}</div>}

      {value.frequency === 'weekly' && (
        <div className="space-y-2">
          <Label>{t('frontier.editor.weekdays')}</Label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_CODES.map((code) => {
              const active = value.weekdays.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  data-testid={`schedule-weekday-${code}`}
                  aria-pressed={active}
                  onClick={() =>
                    set(
                      'weekdays',
                      active
                        ? value.weekdays.filter((day) => day !== code)
                        : [...value.weekdays, code],
                    )
                  }
                  className={`rounded-full border px-3 py-1 text-sm ${
                    active
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200'
                      : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {t(WEEKDAY_KEYS[code])}
                </button>
              );
            })}
          </div>
          {timeField}
        </div>
      )}

      {value.frequency === 'monthly' && (
        <div className="space-y-3">
          <Label>{t('frontier.editor.monthlyMode')}</Label>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="monthly-mode"
                checked={value.monthlyMode === 'day'}
                onChange={() => set('monthlyMode', 'day')}
              />
              {t('frontier.editor.monthlyDay')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="monthly-mode"
                checked={value.monthlyMode === 'last'}
                onChange={() => set('monthlyMode', 'last')}
              />
              {t('frontier.editor.monthlyLast')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="monthly-mode"
                checked={value.monthlyMode === 'nth'}
                onChange={() => set('monthlyMode', 'nth')}
              />
              {t('frontier.editor.monthlyNth')}
            </label>
          </div>
          {value.monthlyMode !== 'last' && (
            <div className="flex flex-wrap items-end gap-3">
              {value.monthlyMode === 'day' ? (
                <div className="space-y-2">
                  <Label htmlFor="schedule-monthday">{t('frontier.editor.day')}</Label>
                  <Input
                    id="schedule-monthday"
                    type="number"
                    min={1}
                    max={31}
                    className="w-24"
                    value={value.monthDay}
                    onChange={(event) => set('monthDay', Number(event.target.value))}
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>{t('frontier.editor.nth')}</Label>
                    <Select
                      value={String(value.nth)}
                      onValueChange={(next) =>
                        set('nth', Number(next) as ScheduleBuilderState['nth'])
                      }
                      options={[
                        { value: '1', label: t('frontier.editor.nthFirst') },
                        { value: '2', label: t('frontier.editor.nthSecond') },
                        { value: '3', label: t('frontier.editor.nthThird') },
                        { value: '4', label: t('frontier.editor.nthFourth') },
                        { value: '-1', label: t('frontier.editor.nthLast') },
                      ]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('frontier.editor.weekdays')}</Label>
                    <Select
                      value={value.nthWeekday}
                      onValueChange={(next) => set('nthWeekday', next)}
                      options={WEEKDAY_CODES.map((code) => ({
                        value: code,
                        label: t(WEEKDAY_KEYS[code]),
                      }))}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="max-w-xs">{timeField}</div>
        </div>
      )}

      {value.frequency === 'yearly' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2">
              <Label htmlFor="schedule-month">{t('frontier.editor.month')}</Label>
              <Select
                value={String(value.yearMonth)}
                onValueChange={(next) => set('yearMonth', Number(next))}
                options={Array.from({ length: 12 }, (_, index) => ({
                  value: String(index + 1),
                  label: new Intl.DateTimeFormat(locale, { month: 'long' }).format(
                    new Date(Date.UTC(2026, index, 1)),
                  ),
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-yearday">{t('frontier.editor.day')}</Label>
              <Input
                id="schedule-yearday"
                type="number"
                min={1}
                max={31}
                className="w-24"
                value={value.yearDay}
                onChange={(event) => set('yearDay', Number(event.target.value))}
              />
            </div>
            {timeField}
          </div>
        </div>
      )}

      {value.frequency !== 'once' && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="schedule-start">{t('frontier.editor.startDate')}</Label>
            <Input
              id="schedule-start"
              type="date"
              value={value.startDate}
              onChange={(event) => set('startDate', event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="schedule-end">{t('frontier.editor.end')}</Label>
            <Select
              value={value.end}
              onValueChange={(next) => set('end', next as ScheduleBuilderState['end'])}
              options={[
                { value: 'never', label: t('frontier.editor.endNever') },
                { value: 'until', label: t('frontier.editor.endUntil') },
                { value: 'count', label: t('frontier.editor.endCount') },
              ]}
            />
          </div>
        </div>
      )}

      {value.frequency !== 'once' && value.end === 'until' && (
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="schedule-until">{t('frontier.editor.endDate')}</Label>
          <Input
            id="schedule-until"
            type="date"
            value={value.untilDate}
            onChange={(event) => set('untilDate', event.target.value)}
          />
        </div>
      )}

      {value.frequency !== 'once' && value.end === 'count' && (
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="schedule-count">{t('frontier.editor.occurrences')}</Label>
          <Input
            id="schedule-count"
            type="number"
            min={1}
            value={value.count}
            onChange={(event) => set('count', Number(event.target.value))}
          />
        </div>
      )}

      <div
        className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900"
        data-testid="schedule-summary"
      >
        <p className="font-medium">{t('frontier.editor.summary')}</p>
        <p className="mt-1 text-gray-600 dark:text-gray-300">
          {spec ? summary : t('frontier.editor.scheduleInvalid')}
        </p>
        {spec && (
          <div className="mt-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('frontier.editor.nextRuns')}
            </p>
            {preview.isPending && !previewData ? (
              <Spinner size="sm" />
            ) : previewError ? (
              <p className="text-xs text-red-600">{previewError}</p>
            ) : (
              <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-gray-300">
                {(previewData?.next_occurrences ?? []).map((occurrence) => (
                  <li key={occurrence} data-testid="schedule-occurrence">
                    {formatOccurrence(occurrence)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function renderSummary(
  t: (key: string, params?: Record<string, string | number>) => string,
  locale: string,
  state: ScheduleBuilderState,
): string {
  const time = state.time || '--:--';
  const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const untilSuffix =
    state.end === 'until' && state.untilDate
      ? t('frontier.editor.untilSuffix', {
          date: dateFormatter.format(new Date(`${state.untilDate}T12:00:00Z`)),
        })
      : '';
  const countSuffix =
    state.end === 'count'
      ? t('frontier.editor.countSuffix', { count: state.count })
      : '';

  switch (state.frequency) {
    case 'once':
      return (
        t('frontier.editor.summaryOnce', {
          date: state.date ? dateFormatter.format(new Date(`${state.date}T12:00:00Z`)) : '—',
          time,
          timezone: state.timezone,
        }) + countSuffix
      );
    case 'minutes':
      return t('frontier.editor.summaryMinutes', {
        count: state.interval,
        timezone: state.timezone,
      });
    case 'hours':
      return t('frontier.editor.summaryHours', {
        count: state.interval,
        timezone: state.timezone,
      });
    case 'weekly':
      return (
        t('frontier.editor.summaryWeekly', {
          days: state.weekdays.map((day) => t(WEEKDAY_KEYS[day] ?? day)).join(', '),
          time,
          timezone: state.timezone,
        }) + untilSuffix + countSuffix
      );
    case 'monthly':
      if (state.monthlyMode === 'last') {
        return (
          t('frontier.editor.summaryMonthlyLast', { time, timezone: state.timezone }) +
          untilSuffix + countSuffix
        );
      }
      if (state.monthlyMode === 'nth') {
        return (
          t('frontier.editor.summaryMonthlyNth', {
            nth: t(
              state.nth === -1
                ? 'frontier.editor.nthLast'
                : (['frontier.editor.nthFirst', 'frontier.editor.nthSecond', 'frontier.editor.nthThird', 'frontier.editor.nthFourth'][state.nth - 1] ?? 'frontier.editor.nthFirst'),
            ),
            weekday: t(WEEKDAY_KEYS[state.nthWeekday] ?? state.nthWeekday),
            time,
            timezone: state.timezone,
          }) + untilSuffix + countSuffix
        );
      }
      return (
        t('frontier.editor.summaryMonthlyDay', {
          day: state.monthDay,
          time,
          timezone: state.timezone,
        }) + untilSuffix + countSuffix
      );
    case 'yearly': {
      const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
        new Date(Date.UTC(2026, state.yearMonth - 1, 1)),
      );
      return (
        t('frontier.editor.summaryYearly', {
          day: state.yearDay,
          month,
          time,
          timezone: state.timezone,
        }) + untilSuffix + countSuffix
      );
    }
    default:
      return (
        t('frontier.editor.summaryDaily', { time, timezone: state.timezone }) +
        untilSuffix + countSuffix
      );
  }
}
